/**
 * Cross-tab messaging over one `BroadcastChannel` ("vnext"). The leader
 * tab (see `useTabLeader`) fans shell-level poll results out here so N
 * open tabs cost one request, not N. Message kinds are keyed so future
 * event streams (SSE fan-out) ride the same channel.
 *
 * Feature-detected: without `BroadcastChannel` nothing is shared and every
 * tab fends for itself — exactly the behaviour before coordination.
 */

export type TabMessage =
  /** The leader's latest value for a poll key. */
  | { type: 'poll-result'; key: string; value: unknown; at: number }
  /** A tab that just became a follower asks the leader to re-send. */
  | { type: 'poll-request'; key: string }
  /** A follower's `refetch()`: the leader fetches now and broadcasts. */
  | { type: 'poll-refetch'; key: string };

const CHANNEL_NAME = 'vnext';

type Handler = (message: TabMessage) => void;

const handlers = new Set<Handler>();
let channel: BroadcastChannel | null | undefined;

export function isTabChannelAvailable(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

function isTabMessage(data: unknown): data is TabMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { type?: unknown }).type === 'string' &&
    typeof (data as { key?: unknown }).key === 'string'
  );
}

function getChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    channel = isTabChannelAvailable() ? new BroadcastChannel(CHANNEL_NAME) : null;
    channel?.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (!isTabMessage(event.data)) {
        return;
      }
      // Copy: a handler may unsubscribe while we iterate.
      for (const handler of [...handlers]) {
        handler(event.data);
      }
    });
  }
  return channel;
}

/** No-op when the channel is unavailable. Never delivered to the sender. */
export function postTabMessage(message: TabMessage): void {
  getChannel()?.postMessage(message);
}

export function subscribeTabMessages(handler: Handler): () => void {
  getChannel();
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}
